import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Grid,
  Stack,
  Typography,
} from "@mui/material";

import { useLazyQuery, useMutation } from "@apollo/client";
import { useAlert } from "react-alert";
import { FormattedMessage, useIntl } from "react-intl";

import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import AddressFields from "./AddressFields";

import {
  GET_COMPANY_ADDRESS_FIELD_SETTINGS_AND_VALUES,
} from "../../../../../graphql/queries/CompanyAdminContext";

import {
  UPDATE_COMPANY_ADDRESS_SETTINGS, UPDATE_COMPANY_ADDRESS_SETTINGS_AND_VALUES,
} from "../../../../../graphql/mutations/CompanyAdminContext";

const ui = {
  page: "#f7f8fa",
  panel: "#ffffff",
  border: "#e6e9ee",
  borderStrong: "#d9dee7",
  text: "#182230",
  muted: "#667085",
  subtle: "#98a2b3",
  soft: "#f8fafc",
};

const clone = value =>
  JSON.parse(JSON.stringify(value));

const ADDRESS_TYPES = [
  {
    name: "deliveryAddress",
    label: "cart.addressDelivery",
  },
  {
    name: "postalAddress",
    label: "cart.addressPostal",
  },
  {
    name: "invoiceAddress",
    label: "cart.addressInvoice",
  },
  {
    name: "visitingAddress",
    label: "cart.addressVisiting",
  },
];

const ADDRESS_VALUE_FIELDS = [
  "address1",
  "address2",
  "address3",
  "address4",
  "address5",
  "city",
  "contact",
  "country",
  "customerId",
  "email",
  "reference",
  "telephone",
  "zip",
];

const buildAddressValuesInput = values =>
  Object.fromEntries(
    ADDRESS_VALUE_FIELDS.map(field => [
      field,
      values?.[field] ?? null,
    ])
  );

const buildSettingsInput = fields =>
  Object.fromEntries(
    Object.entries(fields)
      .sort(
        ([, a], [, b]) =>
          a.displayOrder - b.displayOrder
      )
      .map(([name, field], index) => [
        name,
        {
          displayOrder: index,
          label: field.label || "",
          type: field.type || "OPTIONAL"
        },
      ])
  );

const createAddressState = response =>
  ADDRESS_TYPES.map(address => ({
    name: address.name,
    label: address.label,
    fields: response[`${address.name}Settings`],
    values: response[address.name],
  }));


const AddressLabels = ({ companyId }) => {
  const alert = useAlert();
  const intl = useIntl();

  const [
    getAddressSettings,
    {
      data: addressSettingsData,
      loading: loadingAddressSettings,
    },
  ] = useLazyQuery(
    GET_COMPANY_ADDRESS_FIELD_SETTINGS_AND_VALUES
  );

  const [
    updateAddressSettingsAndValues,
    {
      loading: savingSettings,
    },
  ] = useMutation(
    UPDATE_COMPANY_ADDRESS_SETTINGS_AND_VALUES
  );

  const [addresses, setAddresses] = useState([]);
  const [initialAddresses, setInitialAddresses] =
    useState([]);

  // Enkel dirty-flagga istället för deep-compare på varje render.
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!companyId) {
      return;
    }

    getAddressSettings({
      variables: {
        companyId,
      },
    });
  }, [
    companyId,
    getAddressSettings,
  ]);

  useEffect(() => {
    const response =
      addressSettingsData
        ?.companyAdminContext
        ?.addressSettings
        ?.getCompanyAddressFieldSettingsAndValues;

    if (!response) {
      return;
    }

    const nextAddresses =
      createAddressState(response);

    setAddresses(nextAddresses);
    setInitialAddresses(clone(nextAddresses));
    setIsDirty(false);
  }, [addressSettingsData]);

  const handleFieldConfigSave = useCallback((
    addressName,
    fieldName,
    config
  ) => {
    setAddresses(current =>
      current.map(address => {
        if (address.name !== addressName) {
          return address;
        }

        return {
          ...address,
          fields: {
            ...address.fields,
            [fieldName]: {
              ...address.fields[fieldName],
              label: config.label,
              type: config.type,
            },
          },
        };
      })
    );
    setIsDirty(true);
  }, []);

  const handleValueChange = useCallback((
    addressName,
    fieldName,
    value
  ) => {
    setAddresses(current =>
      current.map(address => {
        if (address.name !== addressName) {
          return address;
        }

        return {
          ...address,
          values: {
            ...address.values,
            [fieldName]: value,
          },
        };
      })
    );
    setIsDirty(true);
  }, []);

  const onDragEnd = useCallback((
    addressName,
    result
  ) => {
    if (!result.destination) {
      return;
    }

    setAddresses(current =>
      current.map(address => {
        if (address.name !== addressName) {
          return address;
        }

        const sortedFields =
          Object.entries(address.fields)
            .sort(
              ([, a], [, b]) =>
                a.displayOrder -
                b.displayOrder
            );

        const [moved] =
          sortedFields.splice(
            result.source.index,
            1
          );

        sortedFields.splice(
          result.destination.index,
          0,
          moved
        );

        return {
          ...address,
          fields: Object.fromEntries(
            sortedFields.map(
              ([fieldName, field], index) => [
                fieldName,
                {
                  ...field,
                  displayOrder: index,
                },
              ]
            )
          ),
        };
      })
    );
    setIsDirty(true);
  }, []);

  const handleReset = useCallback(() => {
    setAddresses(
      clone(initialAddresses)
    );
    setIsDirty(false);
  }, [initialAddresses]);

  const getColumnTitle = address =>
    intl.formatMessage({
      id: address.label,
    });

  const handleSave = async () => {
    try {
      const findAddress = name =>
        addresses.find(
          address => address.name === name
        );

      const settings = {
        companyId,

        deliveryAddressSettings: buildSettingsInput(
          findAddress("deliveryAddress")?.fields || {}
        ),
        postalAddressSettings: buildSettingsInput(
          findAddress("postalAddress")?.fields || {}
        ),
        invoiceAddressSettings: buildSettingsInput(
          findAddress("invoiceAddress")?.fields || {}
        ),
        visitingAddressSettings: buildSettingsInput(
          findAddress("visitingAddress")?.fields || {}
        ),
      };

      const address = {
        companyId,

        deliveryAddress: buildAddressValuesInput(
          findAddress("deliveryAddress")?.values
        ),
        postalAddress: buildAddressValuesInput(
          findAddress("postalAddress")?.values
        ),
        invoiceAddress: buildAddressValuesInput(
          findAddress("invoiceAddress")?.values
        ),
        visitingAddress: buildAddressValuesInput(
          findAddress("visitingAddress")?.values
        ),
      };

      const response =
        await updateAddressSettingsAndValues({
          variables: {
            settings,
            address,
          },
        });

      const result =
        response?.data
          ?.companyAdminContext
          ?.companyAddressSettings
          ?.updateCompanyAddressesAndSettings;

      if (!result?.success) {
        alert.error(
          <FormattedMessage
            id="common.genericErrorMessage"
          />
        );
        return;
      }

      setInitialAddresses(
        clone(addresses)
      );
      setIsDirty(false);

      alert.success(
        <FormattedMessage
          id="common.genericSuccess"
        />
      );
    } catch (error) {
      console.error(
        "Address settings and values save failed",
        error
      );

      alert.error(
        <FormattedMessage
          id="common.genericErrorMessage"
        />
      );
    }
  };

  return (
    <Box>
      <Stack
        direction={{
          xs: "column",
          sm: "row",
        }}
        spacing={1}
        alignItems={{
          xs: "stretch",
          sm: "flex-start",
        }}
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography
            sx={{
              color: ui.text,
              fontSize: 19.5,
              lineHeight: 1.25,
              fontWeight: 650,
              letterSpacing: "-0.012em",
            }}
          >
            Organisationens adresser
          </Typography>

          <Typography
            sx={{
              mt: 0.3,
              color: ui.muted,
              fontSize: 10.8,
              lineHeight: 1.45,
            }}
          >
            Hantera innehåll, fältnamn, ordning och
            egenskaper för organisationens adresser.
            Uppgifterna används bland annat vid
            fakturering och i varukorgar.
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={0.7}
          justifyContent="flex-end"
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={
              <RestartAltRoundedIcon
                sx={{
                  fontSize:
                    "15px !important",
                }}
              />
            }
            disabled={
              !isDirty ||
              loadingAddressSettings ||
              savingSettings
            }
            onClick={handleReset}
            sx={{
              minHeight: 34,
              px: 1.2,
              borderRadius: 1,
              borderColor:
                ui.borderStrong,
              color: ui.text,
              textTransform: "none",
              fontSize: 10.2,
              fontWeight: 600,
              "&:hover": {
                borderColor: "#c5ccd6",
                bgcolor: ui.soft,
              },
            }}
          >
            Återställ ändringar
          </Button>

          <Button
            size="small"
            variant="contained"
            startIcon={
              <SaveOutlinedIcon
                sx={{
                  fontSize:
                    "15px !important",
                }}
              />
            }
            disabled={
              !companyId ||
              !isDirty ||
              loadingAddressSettings ||
              savingSettings
            }
            onClick={handleSave}
            sx={{
              minHeight: 34,
              px: 1.35,
              borderRadius: 1,
              textTransform: "none",
              fontSize: 10.2,
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": {
                boxShadow:
                  "0 2px 6px rgba(16,24,40,.12)",
              },
            }}
          >
            Spara ändringar
          </Button>
        </Stack>
      </Stack>

      <Grid
        container
        spacing={0.9}
        alignItems="stretch"
      >
        {loadingAddressSettings &&
          Array.from({ length: 4 }).map(
            (_, index) => (
              <Grid
                key={index}
                item
                xs={12}
                md={6}
                xl={3}
              >
                <Box
                  sx={{
                    height: 420,
                    border:
                      `1px solid ${ui.border}`,
                    borderRadius: 1.5,
                    bgcolor: ui.soft,
                  }}
                />
              </Grid>
            )
          )}

        {!loadingAddressSettings &&
          addresses.map(
            (address, index) => (
              <AddressFields
                key={address.name}
                name={address.name}
                label={getColumnTitle(
                  address
                )}
                fields={address.fields}
                fieldValues={address.values}
                handleFieldConfigSave={
                  handleFieldConfigSave
                }
                handleValueChange={
                  handleValueChange
                }
                onDragEnd={onDragEnd}
                mainIndex={index}
              />
            )
          )}
      </Grid>
    </Box>
  );
};

AddressLabels.defaultProps = {
  companyId: null,
};

AddressLabels.propTypes = {
  companyId: PropTypes.number,
};

export default AddressLabels;